import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { transportApi } from '@/services/transportApi';
import { AlertRecipients } from '@/components/transport/AlertRecipients';
import { OfficeNetworks } from '@/components/transport/OfficeNetworks';

const Settings = () => {
  const navigate = useNavigate();
  const [includeHired, setIncludeHired] = useState(true);
  const [threshold, setThreshold] = useState(25);
  const [longKm, setLongKm] = useState(50);
  const [pairingMode, setPairingMode] = useState('fairness');
  const [types, setTypes] = useState([]);
  const [newType, setNewType] = useState('');
  const [alertIds, setAlertIds] = useState([]);
  const [offices, setOffices] = useState([]);
  const [lowPct, setLowPct] = useState(20);

  const load = async () => {
    const s = await transportApi.getSettings();
    setIncludeHired(s.include_hired); setThreshold(s.deviation_threshold_pct); setTypes(s.vehicle_types || []);
    setLongKm(s.long_trip_threshold_km || 50);
    setPairingMode(s.driver_pairing_mode || 'fairness');
    setAlertIds(s.alert_user_ids || []);
    setOffices(s.office_networks || []);
    setLowPct(s.low_mileage_drop_pct ?? 20);
  };
  useEffect(() => { load().catch(() => toast.error('Failed to load settings')); }, []);

  const save = async () => {
    try { await transportApi.saveSettings({ include_hired: includeHired, deviation_threshold_pct: Number(threshold), vehicle_types: types, long_trip_threshold_km: Number(longKm), driver_pairing_mode: pairingMode, alert_user_ids: alertIds, office_networks: offices, low_mileage_drop_pct: Number(lowPct) || 0 }); toast.success('Settings saved'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const addType = () => { const t = newType.trim(); if (t && !types.includes(t)) { setTypes([...types, t]); setNewType(''); } };

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="transport-settings-page">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-3xl mx-auto px-4 md:px-6 h-16 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/sections/transport')} className="text-[#F9FAFB] hover:bg-white/10" data-testid="back-button"><ArrowLeft className="w-5 h-5" /></Button>
          <h1 className="text-xl font-bold font-display">Transport Settings</h1>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 md:px-6 py-6 space-y-6">
        <Card className="border-0 shadow-md"><CardContent className="p-5 flex items-center justify-between">
          <div>
            <h3 className="font-semibold">Include Hired Vehicles in Fleet Portfolio</h3>
            <p className="text-sm text-gray-500">When on, hired/rented vehicles can be auto-assigned and count in reports.</p>
          </div>
          <Switch checked={includeHired} onCheckedChange={setIncludeHired} data-testid="settings-include-hired" />
        </CardContent></Card>

        <Card className="border-0 shadow-md"><CardContent className="p-5">
          <h3 className="font-semibold">Route Deviation Threshold</h3>
          <p className="text-sm text-gray-500 mb-2">Flag a trip when its time deviates beyond this % of the route's rolling average.</p>
          <div className="flex items-center gap-2 max-w-[160px]">
            <Input type="number" value={threshold} onChange={e => setThreshold(e.target.value)} data-testid="settings-threshold-input" />
            <span className="text-gray-500">%</span>
          </div>
        </CardContent></Card>

        <Card className="border-0 shadow-md"><CardContent className="p-5">
          <h3 className="font-semibold">Long-Trip Threshold (Smart Dispatch)</h3>
          <p className="text-sm text-gray-500 mb-2">Trips with an estimated distance at or above this count as "long" — the system then prefers a hired day-rent vehicle. Shorter trips prefer owned vehicles.</p>
          <div className="flex items-center gap-2 max-w-[160px]">
            <Input type="number" value={longKm} onChange={e => setLongKm(e.target.value)} data-testid="settings-longkm-input" />
            <span className="text-gray-500">km</span>
          </div>
        </CardContent></Card>

        <Card className="border-0 shadow-md"><CardContent className="p-5">
          <h3 className="font-semibold">Driver Pairing (default)</h3>
          <p className="text-sm text-gray-500 mb-3">How auto-assign picks the driver for a vehicle. <b>Fairness</b> rotates work to the driver with the fewest km this month. <b>Nearest</b> picks the driver physically closest to the vehicle (speed over rotation). You can still override per request.</p>
          <div className="inline-flex rounded-lg overflow-hidden border" data-testid="settings-pairing-toggle">
            <button type="button" onClick={() => setPairingMode('fairness')} className={`px-4 py-2 text-sm ${pairingMode === 'fairness' ? 'bg-amber-500 text-white' : 'bg-white text-gray-600'}`} data-testid="settings-pairing-fairness">Fairness (rotate km)</button>
            <button type="button" onClick={() => setPairingMode('nearest')} className={`px-4 py-2 text-sm ${pairingMode === 'nearest' ? 'bg-amber-500 text-white' : 'bg-white text-gray-600'}`} data-testid="settings-pairing-nearest">Nearest (speed)</button>
          </div>
        </CardContent></Card>

        <Card className="border-0 shadow-md"><CardContent className="p-5">
          <h3 className="font-semibold mb-2">Vehicle Types</h3>
          <div className="flex flex-wrap gap-2 mb-3" data-testid="settings-types-list">
            {types.map(t => (
              <Badge key={t} variant="secondary" className="pl-3 pr-1 py-1 text-sm">
                {t}<button onClick={() => setTypes(types.filter(x => x !== t))} className="ml-1 text-gray-400 hover:text-red-500" data-testid={`settings-type-remove-${t}`}><X className="w-3 h-3" /></button>
              </Badge>
            ))}
          </div>
          <div className="flex gap-2 max-w-sm">
            <Input value={newType} onChange={e => setNewType(e.target.value)} placeholder="New vehicle type" onKeyDown={e => e.key === 'Enter' && addType()} data-testid="settings-type-input" />
            <Button variant="outline" onClick={addType} data-testid="settings-type-add"><Plus className="w-4 h-4" /></Button>
          </div>
        </CardContent></Card>

        <Card className="border-0 shadow-md"><CardContent className="p-5">
          <h3 className="font-semibold">Low Mileage Alert</h3>
          <p className="text-sm text-gray-500 mb-2">When a fuel fill's Km/L is this % below the vehicle's usual Km/L (its last 5 fills, needs at least 3), the alert recipients below get an in-app message and an email from the Gmail connected on the PT Files page. 0 turns it off.</p>
          <div className="flex items-center gap-2 max-w-[160px]">
            <Input type="number" min={0} max={90} value={lowPct} onChange={e => setLowPct(e.target.value)} data-testid="settings-low-mileage-input" />
            <span className="text-gray-500">%</span>
          </div>
        </CardContent></Card>

        <AlertRecipients value={alertIds} onChange={setAlertIds} />
        <OfficeNetworks value={offices} onChange={setOffices} />

        <Button onClick={save} className="bg-amber-500 hover:bg-amber-600" data-testid="settings-save-btn">Save Settings</Button>
      </main>
    </div>
  );
};

export default Settings;
